/**
 * The facts about a person, read as a list and edited in place. One Edit for the whole card: a
 * person's details are usually corrected together on a call ("we moved, and my email changed"),
 * and one save means one version check and one entry in the history.
 */
import {
  ageOn,
  HOUSEHOLD_ROLES,
  householdRoleLabel,
  LANGUAGES,
  languageLabel,
  LIFECYCLE_STAGES,
  lifecycleLabel,
  SEXES,
  sexLabel,
  stateLabel,
  toUsDate,
  US_STATES,
  type Contact,
} from "@tlcrm/shared";
import { useState } from "react";

import { ApiError } from "../../api/client";
import { useUpdateContact, useUsers } from "../../api/hooks";
import { useMe } from "../../auth/session";
import { useToast } from "../../components/Toasts";
import { FieldBox, Select } from "../../components/ui";
import { useForm } from "../../forms/useForm";
import { height } from "../../lib/format";

function Fv({
  label,
  value,
  full,
}: {
  label: string;
  value: string | null | undefined;
  full?: boolean;
}) {
  return (
    <div className={`fv${full ? " full" : ""}`}>
      <dt>{label}</dt>
      <dd className={value ? undefined : "empty-v"}>{value || "–"}</dd>
    </div>
  );
}

const yesNo = (v: boolean | null) => (v === null ? null : v ? "Yes" : "No");

function formFrom(c: Contact) {
  return {
    firstName: c.firstName,
    lastName: c.lastName,
    lifecycleStage: c.lifecycleStage,
    ownerId: c.owner?.id ?? "",
    email: c.email ?? "",
    dateOfBirth: toUsDate(c.dateOfBirth),
    sex: (c.sex ?? "") as string,
    tobaccoUse: c.tobaccoUse === null ? "" : c.tobaccoUse ? "yes" : "no",
    heightInches: c.heightInches?.toString() ?? "",
    weightPounds: c.weightPounds?.toString() ?? "",
    language: c.language,
    street: c.street ?? "",
    city: c.city ?? "",
    state: (c.state ?? "") as string,
    postalCode: c.postalCode ?? "",
    householdRole: (c.householdRole ?? "") as string,
    doNotEmail: c.doNotEmail,
    doNotText: c.doNotText,
  };
}

export function ContactDetails({ contact }: { contact: Contact }) {
  const [editing, setEditing] = useState(false);
  return editing ? (
    <EditDetails contact={contact} onDone={() => setEditing(false)} />
  ) : (
    <div className="card">
      <div className="card-head">
        <h3>Details</h3>
        <button className="btn btn-sm" onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>
      <div className="card-body">
        <dl className="fields" style={{ margin: 0 }}>
          <Fv label="Stage" value={lifecycleLabel(contact.lifecycleStage)} />
          <Fv label="Owner" value={contact.owner?.fullName ?? "Unassigned"} />
          <Fv label="Email" value={contact.email} full />
          <Fv
            label="Date of birth"
            value={
              contact.dateOfBirth
                ? `${toUsDate(contact.dateOfBirth)} · ${ageOn(contact.dateOfBirth)}`
                : null
            }
          />
          <Fv label="Language" value={languageLabel(contact.language)} />
          <Fv label="Sex" value={sexLabel(contact.sex)} />
          <Fv label="Tobacco" value={yesNo(contact.tobaccoUse)} />
          <Fv label="Height" value={contact.heightInches ? height(contact.heightInches) : null} />
          <Fv label="Weight" value={contact.weightPounds ? `${contact.weightPounds} lb` : null} />
          <Fv
            label="Address"
            full
            value={
              [
                contact.street,
                [contact.city, contact.state && stateLabel(contact.state)]
                  .filter(Boolean)
                  .join(", "),
                contact.postalCode,
              ]
                .filter(Boolean)
                .join(" · ") || null
            }
          />
          {contact.householdId && (
            <Fv label="In household as" value={householdRoleLabel(contact.householdRole)} />
          )}
          <Fv
            label="Contact preferences"
            full
            value={
              [
                contact.doNotCall && "Do not call",
                contact.doNotEmail && "Do not email",
                contact.doNotText && "Do not text",
              ]
                .filter(Boolean)
                .join(", ") || "Any channel"
            }
          />
        </dl>
      </div>
    </div>
  );
}

function EditDetails({ contact, onDone }: { contact: Contact; onDone: () => void }) {
  const { can } = useMe();
  const users = useUsers();
  const update = useUpdateContact(contact.id);
  const toast = useToast();
  const f = useForm(formFrom(contact));
  const [stale, setStale] = useState(false);
  const v = f.values;

  const save = async () => {
    const num = (s: string) => (s.trim() ? Number(s) : null);
    try {
      await update.mutateAsync({
        version: contact.version,
        firstName: v.firstName,
        lastName: v.lastName,
        lifecycleStage: v.lifecycleStage,
        ...(v.ownerId !== (contact.owner?.id ?? "") && { ownerId: v.ownerId || null }),
        email: v.email,
        dateOfBirth: v.dateOfBirth,
        sex: v.sex || null,
        tobaccoUse: v.tobaccoUse === "" ? null : v.tobaccoUse === "yes",
        heightInches: num(v.heightInches),
        weightPounds: num(v.weightPounds),
        language: v.language,
        street: v.street,
        city: v.city,
        state: v.state || null,
        postalCode: v.postalCode,
        ...(contact.householdId && { householdRole: v.householdRole || null }),
        doNotEmail: v.doNotEmail,
        doNotText: v.doNotText,
      });
      toast("Saved.");
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "stale_version") setStale(true);
      else f.fail(err);
    }
  };

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="card-head">
        <h3>Edit details</h3>
      </div>
      <div className="card-body stack">
        {stale && (
          <div className="banner banner-ochre">
            Someone saved this contact while you were editing. Cancel to see their changes, then
            make yours again.
          </div>
        )}
        {f.formError && <div className="banner banner-brick">{f.formError}</div>}
        <div className="form-grid">
          <FieldBox label="First name" htmlFor="cd-first" error={f.errors.firstName}>
            <input id="cd-first" {...f.text("firstName")} />
          </FieldBox>
          <FieldBox label="Last name" htmlFor="cd-last" error={f.errors.lastName}>
            <input id="cd-last" {...f.text("lastName")} />
          </FieldBox>
          <FieldBox label="Stage" htmlFor="cd-stage">
            <Select
              id="cd-stage"
              value={v.lifecycleStage}
              onChange={(x) => f.set("lifecycleStage")(x || "prospect")}
              options={LIFECYCLE_STAGES}
            />
          </FieldBox>
          <FieldBox label="Owner" htmlFor="cd-owner" error={f.errors.ownerId}>
            <Select
              id="cd-owner"
              value={v.ownerId}
              onChange={f.set("ownerId")}
              options={(users.data ?? []).map((u) => [u.id, u.fullName] as const)}
              empty="Unassigned"
            />
          </FieldBox>
          <FieldBox label="Email" htmlFor="cd-email" error={f.errors.email} full>
            <input id="cd-email" type="email" {...f.text("email")} />
          </FieldBox>
          <FieldBox label="Date of birth" htmlFor="cd-dob" error={f.errors.dateOfBirth}>
            <input
              id="cd-dob"
              inputMode="numeric"
              placeholder="MM-DD-YYYY"
              {...f.text("dateOfBirth", "mono")}
            />
          </FieldBox>
          <FieldBox label="Language" htmlFor="cd-lang">
            <Select
              id="cd-lang"
              value={v.language}
              onChange={(x) => f.set("language")(x || "en")}
              options={LANGUAGES}
            />
          </FieldBox>
          <FieldBox label="Sex" htmlFor="cd-sex">
            <Select
              id="cd-sex"
              value={v.sex}
              onChange={f.set("sex")}
              options={SEXES}
              empty="Not known"
            />
          </FieldBox>
          <FieldBox label="Tobacco" htmlFor="cd-tob">
            <Select
              id="cd-tob"
              value={v.tobaccoUse}
              onChange={f.set("tobaccoUse")}
              options={[
                ["no", "No"],
                ["yes", "Yes"],
              ]}
              empty="Not asked"
            />
          </FieldBox>
          <FieldBox label="Height (inches)" htmlFor="cd-h" error={f.errors.heightInches}>
            <input id="cd-h" inputMode="numeric" {...f.text("heightInches", "mono")} />
          </FieldBox>
          <FieldBox label="Weight (lb)" htmlFor="cd-w" error={f.errors.weightPounds}>
            <input id="cd-w" inputMode="numeric" {...f.text("weightPounds", "mono")} />
          </FieldBox>
          <FieldBox label="Street" htmlFor="cd-street" full>
            <input id="cd-street" {...f.text("street")} />
          </FieldBox>
          <FieldBox label="City" htmlFor="cd-city">
            <input id="cd-city" {...f.text("city")} />
          </FieldBox>
          <FieldBox label="State" htmlFor="cd-state" error={f.errors.state}>
            <Select
              id="cd-state"
              value={v.state}
              onChange={f.set("state")}
              options={US_STATES}
              empty="Not known"
            />
          </FieldBox>
          <FieldBox label="ZIP" htmlFor="cd-zip" error={f.errors.postalCode}>
            <input id="cd-zip" inputMode="numeric" {...f.text("postalCode", "mono")} />
          </FieldBox>
          {contact.householdId && (
            <FieldBox label="Household role" htmlFor="cd-hhr">
              <Select
                id="cd-hhr"
                value={v.householdRole}
                onChange={f.set("householdRole")}
                options={HOUSEHOLD_ROLES}
                empty="Not set"
              />
            </FieldBox>
          )}
          <div className="full" style={{ display: "flex", gap: "var(--s-7)" }}>
            <label className="check">
              <input
                type="checkbox"
                checked={v.doNotEmail}
                onChange={(e) => f.set("doNotEmail")(e.target.checked)}
              />{" "}
              Do not email
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={v.doNotText}
                onChange={(e) => f.set("doNotText")(e.target.checked)}
              />{" "}
              Do not text
            </label>
          </div>
        </div>
        {!can("records:reassign") && contact.owner && (
          <p className="t3" style={{ margin: 0, fontSize: "var(--fs-xs)" }}>
            Changing the owner of someone else's contact needs a supervisor.
          </p>
        )}
      </div>
      <div className="card-foot">
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ marginLeft: "auto" }}
          disabled={update.isPending || stale}
        >
          {update.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
